// @ts-nocheck
/*
 * Port of views/dashboard/numbers/number_file_list.js (380 lines).
 * BYTE-IDENTICAL — number-type filter (matches uppercase substring in
 * file_name), multi/single delete via API_NUMBER_FILE_LIST_DELETE.
 * Chrome swap: PrimeReact DataTable → obd3-gui + shadcn Button/Checkbox.
 * DeleteIcon inlined.
 */
import React, { useState, useEffect } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import RenderDeleteModal from '@/components/partials/components/delete-modal';
import { API_NUMBER_FILE_LIST, API_NUMBER_FILE_LIST_DELETE } from '@/service/url';
import { NUMBERS_TYPES } from '@/utilities/constant';
import Loader from '@/components/partials/components/Loader';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { fetchAuthedBlobUrl, revokeAuthedBlobUrl } from '@/utilities/mediaAudio';
import { exportToCSV } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { FileText, Trash2, Download, ExternalLink } from 'lucide-react';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

export default function NumberFileList() {
  const [enableDeleteModal, setEnableDeleteModal] = useState(false);
  const [deletedNumberFileId, setDeleteNumberFileId] = useState(undefined);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [viewingId, setViewingId] = useState<any>(null);

  const handleViewFile = async (row: any) => {
    if (!row?.file_url) return;

    // A third `window.open` features argument (e.g. noopener) makes
    // browsers open a popup. Omit it so this is a normal new tab.
    const tab = window.open('about:blank', '_blank');
    if (tab) {
      try {
        tab.document.title = row.file_name || 'Opening file…';
        tab.document.body.innerHTML =
          '<p style="font-family:sans-serif;padding:24px;color:#64748b">Opening file…</p>';
      } catch {
        /* ignore — tab will still navigate to the blob URL */
      }
    }

    setViewingId(row.id);

    try {
      const { objectUrl } = await fetchAuthedBlobUrl(row.file_url);

      if (tab && !tab.closed) {
        tab.location.replace(objectUrl);
      } else {
        window.location.assign(objectUrl);
      }

      setTimeout(() => revokeAuthedBlobUrl(objectUrl), 60000);
    } catch (error: any) {
      tab?.close();
      toast.error(error?.message || 'Unable to open the file');
    } finally {
      setViewingId(null);
    }
  };
  const [showToast, setShowToast] = useState(false);
  const [numberFileList, setNumberFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [selectedData, setSelectedData] = useState([]);
  const [isMultiDelete, setIsMultiDelete] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [numberTypeFilter, setNumberTypeFilter] = usePersistedState('numbers/NumberFileList:numberTypeFilter', '');
  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] = useState({});

  const filteredFileList = numberTypeFilter
    ? numberFileList.filter((item) => {
        const name = (item.file_name || '').toUpperCase();
        return name.includes(numberTypeFilter.toUpperCase());
      })
    : numberFileList;

  const handleExport = async () => {
    try {
      setButtonDisable(true);
      exportToCSV({
        fileName: 'number-file-list',
        data: filteredFileList,
        columns: [
          { header: 'File Name', accessor: 'file_name' },
          { header: 'File URL', accessor: 'file_url' },
          { header: 'Uploaded By', accessor: (row) => row.user?.username || '-' },
          {
            header: 'Created Date',
            accessor: (row) => new Date(row.created_at).toLocaleString('en-IN', {
              day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
            }),
          },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  const handleCheckboxToggle = (fileId) => {
    const isSelected = selectedData.includes(fileId);
    setSelectedData(isSelected ? selectedData.filter((id) => id !== fileId) : [...selectedData, fileId]);
  };

  const handleMultiDelete = () => {
    setIsMultiDelete(true);
    setEnableDeleteModal(true);
  };

  useEffect(() => {
    setLoading(true);
    getApiCall(API_NUMBER_FILE_LIST)
      .then(onNumberFileListSuccess)
      .catch(onError)
      .finally(() => setLoading(false));
  }, []);

  function onNumberFileListSuccess(resp) {
    const updatedList = resp.map((val, idx) => {
      val['key'] = idx + 1;
      val['created_at'] = new Date(val['created_at']).toGMTString();
      return val;
    });
    setNumberFileList(updatedList);
  }

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings) {
      setModuleOperationsForReadPermissionCheck(parseModuleOperations(user.acl_settings.module_operations));
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed = typeof user.acl_settings.module_operations === 'string'
            ? parseModuleOperations(user.acl_settings.module_operations)
            : user.acl_settings.module_operations;
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  function handleNumberFileListDelete(id) {
    checkAuth(setToastProps, setShowToast);
    setIsMultiDelete(false);
    setEnableDeleteModal(true);
    setDeleteNumberFileId(id);
  }

  const handleSelectAll = () => {
    if (selectedData.length === filteredFileList.length) {
      setSelectedData([]);
    } else {
      setSelectedData(filteredFileList.map((f) => f.id));
    }
  };

  useEffect(() => {
    if (confirmDelete) {
      if (isMultiDelete) {
        postApiCall(API_NUMBER_FILE_LIST_DELETE, { fileListIds: selectedData })
          .then(onDeleteSuccess)
          .catch(onDeleteFail);
      } else {
        postApiCall(API_NUMBER_FILE_LIST_DELETE, { fileListIds: [deletedNumberFileId] })
          .then(onDeleteSuccess)
          .catch(onDeleteFail);
      }
    }
  }, [confirmDelete]);

  function onDeleteSuccess() {
    toast.success('File deleted successfully');
    let updatedList;
    if (isMultiDelete) {
      updatedList = numberFileList
        .filter((val) => !selectedData.includes(val.id))
        .map((val, key) => {
          val['key'] = key + 1;
          return val;
        });
      setSelectedData([]);
    } else {
      updatedList = numberFileList
        .filter((val) => val.id != deletedNumberFileId)
        .map((val, key) => {
          val['key'] = key + 1;
          return val;
        });
    }

    setLoading(true);
    getApiCall(API_NUMBER_FILE_LIST)
      .then(onNumberFileListSuccess)
      .catch(onError)
      .finally(() => setLoading(false));

    setNumberFileList(updatedList);
    setEnableDeleteModal(false);
    setDeleteNumberFileId(undefined);
    setConfirmDelete(false);
  }

  function onDeleteFail(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      toast.error(`Unable to delete File: ${err}`);
    }
    setConfirmDelete(false);
  }

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  if (loading && numberFileList.length === 0) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  const columns: ColumnDef<any>[] = [
    {
      id: '_select',
      size: 44,
      enableSorting: false,
      enableHiding: false,
      header: () => (
        <Checkbox
          checked={selectedData.length === filteredFileList.length && filteredFileList.length > 0}
          onCheckedChange={handleSelectAll}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedData.includes(row.original.id)}
          onCheckedChange={() => handleCheckboxToggle(row.original.id)}
          aria-label="Select row"
        />
      ),
    },
    {
      accessorKey: 'file_name',
      header: 'File Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20">
            <FileText className="h-4 w-4" />
          </span>
          <span className="font-medium text-foreground">
            {row.original.file_name || '—'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'file_url',
      header: 'File URL',
      size: 100,
      cell: ({ row }) => {
        if (!row.original.file_url) {
          return <span className="text-muted-foreground">—</span>;
        }
        return (
          <button
            type="button"
            onClick={() => handleViewFile(row.original)}
            disabled={viewingId === row.original.id}
            title={row.original.file_url}
            className="inline-flex items-center gap-1 text-sm text-primary underline-offset-2 hover:underline disabled:opacity-50"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            {viewingId === row.original.id ? 'Opening…' : 'View'}
          </button>
        );
      },
    },
    {
      accessorFn: (row) => row.user?.username,
      id: 'uploaded_by',
      header: 'Uploaded By',
      cell: ({ row }) => (
        <span className="text-sm text-foreground">
          {row.original.user?.username || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
          {new Date(row.original.created_at).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 90,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <button
          type="button"
          title={`Delete ${row.original.file_name || 'file'}`}
          onClick={() => handleNumberFileListDelete(row.original.id)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-destructive/10 text-destructive transition-colors hover:bg-destructive/15"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      ),
    },
  ];

  // Stat strip data
  const totalFiles = numberFileList.length;
  const distinctUploaders = new Set(
    numberFileList.map((f: any) => f.user?.username).filter(Boolean),
  ).size;

  return (
    <FillPage>
      <PageHeader
        title="Number File Uploads"
        description="CSV / Excel bulk-number upload history and their processing status."
      />

      {/* Filter toolbar — segmented type tabs + bulk actions + stat
          chips inline on one row to reclaim vertical space. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div
          className="inline-flex h-10 items-center gap-1 rounded-full border border-border/60 bg-card p-1"
          style={{
            boxShadow:
              'inset 2px 2px 5px rgba(163,177,198,0.35), inset -2px -2px 5px rgba(255,255,255,0.9)',
          }}
        >
          {[
            { value: '', label: 'All' },
            ...NUMBERS_TYPES.map((v: any) => ({ value: v.value, label: v.label })),
          ].map((t: any) => (
            <button
              key={t.value || 'all'}
              type="button"
              onClick={() => {
                setNumberTypeFilter(t.value);
                setSelectedData([]);
              }}
              className={
                'rounded-full px-4 py-1.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 ' +
                (numberTypeFilter === t.value
                  ? 'bg-primary text-primary-foreground shadow-[0_2px_5px_rgba(0,0,0,0.15)]'
                  : 'text-muted-foreground hover:bg-primary/10 hover:text-primary')
              }
            >
              {t.label}
            </button>
          ))}
        </div>
        <Button
          size="sm"
          variant="destructive"
          onClick={handleMultiDelete}
          disabled={selectedData.length === 0}
          className="gap-1"
        >
          <Trash2 className="h-4 w-4" />
          Delete Selected
          {selectedData.length > 0 && (
            <span className="ml-1 rounded-full bg-white/20 px-1.5 text-[10px] font-bold">
              {selectedData.length}
            </span>
          )}
        </Button>
        {filteredFileList.length > 0 && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleExport}
            disabled={buttonDisable}
            className="gap-1"
          >
            <Download className="h-4 w-4" />
            Export
            {buttonDisable && (
              <span
                className="spinner-border spinner-border-sm ml-2"
                role="status"
                aria-hidden="true"
              />
            )}
          </Button>
        )}

        {/* Stat chips pushed to the right on the same row. */}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1.5 text-xs shadow-neu-raised">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
              <FileText className="h-3.5 w-3.5" />
            </span>
            <span className="text-muted-foreground">Total</span>
            <span className="font-semibold text-foreground tabular-nums">{totalFiles}</span>
          </div>
          {distinctUploaders > 0 && (
            <div className="flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs shadow-neu-raised dark:border-sky-900/60 dark:bg-sky-950/30">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-300">
                <Download className="h-3.5 w-3.5" />
              </span>
              <span className="text-sky-700 dark:text-sky-300">Uploaders</span>
              <span className="font-semibold text-sky-800 tabular-nums dark:text-sky-200">
                {distinctUploaders}
              </span>
            </div>
          )}
        </div>
      </div>

      <DataTable
        tableId="number-file.list:v2"
        columns={columns}
        data={filteredFileList}
        isLoading={loading}
        searchPlaceholder="Search files by name…"
        emptyTitle="No files"
        emptyDescription="No number-file uploads match the current filter."
        pageSize={10}
        initialColumnPinning={{ right: ['actions'] }}
        disableExport
      />

      <RenderDeleteModal
        show={enableDeleteModal}
        setShow={setEnableDeleteModal}
        setConfirmDelete={setConfirmDelete}
        isMultiDelete={isMultiDelete}
        clearSelectedData={() => setSelectedData([])}
      />
    </FillPage>
  );
}
